/**
 * Reusable translucent workspace surface.
 *
 * GlassSurface is deliberately presentation-only. Callers choose the semantic
 * element and supply any accessible name appropriate to their content.
 */
import { forwardRef } from "react";
import type { PaperProps } from "@mui/material";
import { Paper } from "@mui/material";
import { styled } from "@mui/material/styles";

import { reducedMotionMediaQuery } from "../../theme/motionTokens";

export type GlassIntensity = "subtle" | "standard" | "strong";
export type GlassDepth = "flat" | "raised" | "floating";

export interface GlassSurfaceProps
	extends Omit<PaperProps, "elevation" | "variant"> {
	intensity?: GlassIntensity;
	depth?: GlassDepth;
}

const blurByIntensity: Record<GlassIntensity, number> = {
	subtle: 8,
	standard: 14,
	strong: 20,
};

/** Safe fallbacks so unit tests without flahaSoilTheme never throw unhandled errors. */
const FALLBACK_GLASS = {
	subtle: "rgba(22, 28, 24, 0.72)",
	standard: "rgba(22, 28, 24, 0.86)",
	strong: "rgba(26, 33, 28, 0.94)",
	border: "rgba(246, 240, 227, 0.18)",
	borderStrong: "rgba(246, 240, 227, 0.3)",
	shadow: "0 16px 44px rgba(0, 0, 0, 0.28)",
	shadowRaised: "0 24px 72px rgba(0, 0, 0, 0.42)",
} as const;

const GlassPaper = styled(Paper, {
	shouldForwardProp: (prop) => prop !== "intensity" && prop !== "depth",
})<Required<Pick<GlassSurfaceProps, "intensity" | "depth">>>(
	({ theme, intensity, depth }) => {
		const glass = theme.palette.glass ?? FALLBACK_GLASS;
		const workspace = theme.palette.workspace;
		const backgroundColor = glass[intensity] ?? FALLBACK_GLASS[intensity];
		const boxShadow =
			depth === "floating"
				? (glass.shadowRaised ?? FALLBACK_GLASS.shadowRaised)
				: depth === "raised"
					? (glass.shadow ?? FALLBACK_GLASS.shadow)
					: "none";
		const focusBlue = workspace?.blue ?? theme.palette.primary.main;
		const textColor = workspace?.textPrimary ?? theme.palette.text.primary;
		const surfaceTransition =
			theme.motion?.transition?.surface ?? theme.transitions.create("all");
		const reducedTransition =
			theme.motion?.reducedMotion?.transition ?? "none";

		return {
			position: "relative",
			color: textColor,
			// Prefer opaque-enough glass so body text stays readable on
			// layered ambient backgrounds (contrast over pure blur).
			backgroundColor,
			backgroundImage:
				"linear-gradient(145deg, rgba(255,255,255,0.055), rgba(255,255,255,0) 48%)",
			border: `1px solid ${
				intensity === "strong"
					? (glass.borderStrong ?? FALLBACK_GLASS.borderStrong)
					: (glass.border ?? FALLBACK_GLASS.border)
			}`,
			borderRadius: theme.shape.borderRadius * 1.4,
			boxShadow,
			backdropFilter: `blur(${blurByIntensity[intensity]}px) saturate(118%)`,
			WebkitBackdropFilter: `blur(${blurByIntensity[intensity]}px) saturate(118%)`,
			transition: surfaceTransition,
			overflow: "hidden",
			"&:focus-within": {
				borderColor: focusBlue,
				boxShadow:
					depth === "flat"
						? `0 0 0 2px ${focusBlue}`
						: `${boxShadow}, 0 0 0 2px ${focusBlue}`,
			},
			[reducedMotionMediaQuery]: {
				transition: reducedTransition,
			},
		};
	}
);

export const GlassSurface = forwardRef<HTMLDivElement, GlassSurfaceProps>(
	function GlassSurface(
		{ intensity = "standard", depth = "raised", ...props },
		ref
	) {
		return (
			<GlassPaper
				ref={ref}
				elevation={0}
				intensity={intensity}
				depth={depth}
				{...props}
			/>
		);
	}
);
